import React, { KeyboardEvent, memo, useEffect, useRef } from 'react'
import { AddTopBottomBtn } from '../../../../buttons/AddToTopBottomBtn/AddTopBottomBtn'
import AppButton from '../../../../buttons/AppButton/AppButton'
import { getValidationSchema } from '../../utils/getValidationSchema'
import { getNewTableRowData } from '../../utils/getNewTableRowData'
import { useFormValidation } from '../../../../../hooks/useFormValidation'
import * as yup from 'yup'
import { inputCall } from '../../../control'
import { buildControlsExt } from '../../../../inputs/controls'
import { DescriptionBtnWithPopup } from '../../../../buttons/PromptBtn/DescriptionBtnWithPopup'
import styles from './TableAdvanced.module.scss'
import { toSentenceCase } from '../../../../../utils/toSentenceCase'
import { getLabel } from '../../../../../utils/getLabel'
import { COUNTRIES, isCountries } from '../../../../../constants/countries'

interface Props {
    componentSchema: any
    setConfigState: Function
    onScrollIntoRow: (index: number) => void
    configStateRefObj: any
    isEditMode: boolean
    setEditMode: React.Dispatch<React.SetStateAction<boolean>>
    scalarsName?: string
}

export const TableHeadAdvanced = memo(function TableHeadAdvanced({
    componentSchema,
    setConfigState,
    onScrollIntoRow,
    configStateRefObj,
    isEditMode,
    setEditMode,
    scalarsName,
}: Props) {
    const firstInputRef = useRef<HTMLInputElement>(null)

    const names =
        Object.keys(componentSchema).filter((name) => name !== 'element_id') ||
        []
    const validationSchema = getValidationSchema(componentSchema)
    const newRowData = getNewTableRowData(names)
    const [inputState, setInputState, touched, setTouched, validationResult] =
        useFormValidation(newRowData, validationSchema)
    const dataValidationSchema = yup.object().shape(validationSchema)
    const isNewRowValid = dataValidationSchema.isValidSync(inputState)

    const rowDataArray = names.map((el: string, idx: number) =>
        inputCall({
            type: componentSchema[el]['type'],
            name: el,
            isLabelHidden: true,
            data: {},
            schema: componentSchema[el],
            options: isCountries(scalarsName) ? COUNTRIES : null,
            state: inputState,
            isFeedbackFixed: true,
            placeholder: componentSchema[el]?.default ?? '',
            changeWarning: componentSchema[el]?.change_warning,
            ref: idx === 0 ? firstInputRef : null,
            onEnter: handleKeyDown,
        })
    )

    const newRow = buildControlsExt(
        rowDataArray,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    const cleanInputData = () => {
        setTouched({})
        setInputState(getNewTableRowData(names))
    }

    const addRowAtBottom = () => {
        setConfigState(() => configStateRefObj.current)
        setConfigState((prev: any[]) => {
            onScrollIntoRow(prev.length)
            const result = [...prev, inputState]
            configStateRefObj.current = result
            return result
        })
        if (isEditMode) {
            setEditMode(false)
        }
        cleanInputData()
        setTimeout(() => {
            firstInputRef.current?.focus()
        }, 100)
    }

    const addRowAtTop = () => {
        setConfigState(() => configStateRefObj.current)
        setConfigState((prev: any[]) => {
            onScrollIntoRow(0)
            const result = [inputState, ...prev]
            configStateRefObj.current = result
            return result
        })
        if (isEditMode) {
            setEditMode(false)
        }
        cleanInputData()
        setTimeout(() => {
            firstInputRef.current?.focus()
        }, 100)
    }

    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
        if (e?.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            names.forEach((name: string) => {
                setTouched((prev: typeof inputState) => ({
                    ...prev,
                    [name]: true,
                }))
            })
            if (isNewRowValid) {
                addRowAtBottom()
            }
        } else if (e?.key === 'Enter' && e?.shiftKey) {
            e.preventDefault()
            names.forEach((name: string) => {
                setTouched((prev: typeof inputState) => ({
                    ...prev,
                    [name]: true,
                }))
            })
            if (isNewRowValid) {
                addRowAtTop()
            }
        }
    }

    useEffect(() => {
        if (!isEditMode) {
            firstInputRef.current?.focus()
        }
    }, [isEditMode])

    return (
        <>
            <tr className={`${styles.inputsTh}`}>
                <td className={`${styles.inputLabelsTd} bg-white-blue `}>#</td>
                {names.map((name: string) => (
                    <td
                        key={name}
                        className={`${styles.inputLabelsTd} bg-white-blue `}
                    >
                        {toSentenceCase(
                            componentSchema?.[name]?.name || name
                        )}{' '}
                        {componentSchema[name]?.description && (
                            <DescriptionBtnWithPopup
                                type="small"
                                header={getLabel(
                                    name,
                                    componentSchema?.[name]?.name
                                )}
                                description={componentSchema[name]?.description}
                                className="mt-1"
                            />
                        )}
                    </td>
                ))}
                <td className={`${styles.inputLabelsTd} bg-white-blue `}>
                    Actions
                </td>
            </tr>
            <tr className={`${styles.inputsTh}`}>
                <td className="bg-white-blue" />
                {newRow.map(
                    (el: any, i) =>
                        el?.key !== 'element_id' && (
                            <td
                                style={{
                                    verticalAlign: 'top',
                                }}
                                className="pb-3 pt-0 bg-white-blue"
                                key={i}
                            >
                                {el}
                            </td>
                        )
                )}
                <td
                    style={{ verticalAlign: 'top' }}
                    className="bg-white-blue "
                >
                    <div className="d-flex justify-content-center gap-2 ">
                        <AddTopBottomBtn
                            btnType={'top'}
                            onClick={addRowAtTop}
                            className="p-2 rounded"
                            disabled={!isNewRowValid}
                        />
                        <AddTopBottomBtn
                            btnType={'bottom'}
                            onClick={addRowAtBottom}
                            className="p-2 rounded"
                            disabled={!isNewRowValid}
                        />
                        <AppButton
                            variant="cancel"
                            onClick={cleanInputData}
                            className="btn-outline-danger btn btn-add ps-2 pe-2"
                            tooltip="buttons.cancel"
                        />
                    </div>
                </td>
            </tr>
        </>
    )
})
